import type { CandidateBriefApi } from '@/modules/candidate-detail/queries/types';
import type {
  CandidateDecision,
  CandidateDetailData,
} from '@/modules/candidate-detail/types';
import type { CandidateStatusApi } from '@/modules/candidates/types';

const candidateStatuses = [
  'New',
  'Rejected',
  'Shortlisted',
  'Declined',
  'Contacted',
  'Prescreened',
  'PrescreeningScheduled',
  'Submitted',
  'AiPrescreened',
  'Interviewing',
  'Hired',
  'ContactStarted',
  'AssessmentCompleted',
  'ReadyForSubmission',
  'HiringManagerApproved',
] as const satisfies readonly CandidateStatusApi[];

export function parseCandidateStatus(
  status: string,
): CandidateStatusApi | null {
  return (
    candidateStatuses.find((candidateStatus) => candidateStatus === status) ??
    null
  );
}

export function mapCandidateDetail(
  candidate: CandidateBriefApi,
): CandidateDetailData {
  return {
    candidateId: candidate.candidateId,
    name:
      [candidate.firstName, candidate.lastName]
        .map((part) => part.trim())
        .filter(Boolean)
        .join(' ') || 'Candidate',
    firstName: candidate.firstName.trim() || 'this candidate',
    matchedRole: candidate.jobTitle,
    currentRole: candidate.headline?.trim() || candidate.jobTitle,
    location: candidate.location,
    totalExperience: candidate.totalExperience,
    linkedInUrl: candidate.linkedinUrl,
    assessment: candidate.assessment
      ? {
          mode: candidate.assessment.mode,
          summary: candidate.assessment.summary,
          strengths: candidate.assessment.why,
          prescreenSummary: candidate.assessment.prescreenSummary,
          confirmedFacts: candidate.assessment.confirmedFacts,
          openQuestions: candidate.assessment.openQuestions,
          concerns: candidate.assessment.concerns,
          ledgerStage: candidate.assessment.ledgerStage,
          ledger: candidate.assessment.ledger,
          sourcedLedger: candidate.assessment.sourcedLedger,
          scoredLedger: candidate.assessment.scoredLedger,
          atSubmissionLedgerStage: candidate.assessment.atSubmissionLedgerStage,
          atSubmissionLedger: candidate.assessment.atSubmissionLedger,
        }
      : null,
    prescreen: candidate.prescreen,
    candidateJobId: candidate.candidateJobId,
    isSelfServiceJob: candidate.operatingMode === 'SelfService',
    screeningMethod: candidate.screeningMethod,
    sharedPrescreenLinkType: candidate.sharedPrescreenLinkType ?? null,
    prescreenNote: candidate.prescreenNote ?? null,
  };
}

export function decisionFromStatus(
  status: CandidateStatusApi | null,
): CandidateDecision {
  if (status === 'Rejected') return 'rejected';
  if (status === 'Declined') return 'declined';
  if (status === 'Hired') return 'hired';
  if (status === 'HiringManagerApproved') return 'approved';
  if (status === 'Interviewing') return 'interviewing';
  return 'awaiting';
}
